import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Container,
  Box,
  Paper,
  Stack,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { ErrorOutlined as ErrorIcon } from '@mui/icons-material';
import ReportHeaderSection from '../features/report/components/ReportHeaderSection';
import ReportSettingsSection from '../features/report/components/ReportSettingsSection';
import ResultUnitsSection from '../features/report/components/ResultUnitsSection';
import ReportActionsSection from '../features/report/components/ReportActionsSection';
import { useReportScopeData } from '../features/report/context/ReportScopeDataContext';
import { useConfigureReportRequest } from '../features/report/hooks/useConfigureReportRequest';
import { useRunReportJob } from '../features/report/hooks/useRunReportJob';
import { ReportRequest, ReportStatus, ReportTarget } from '../types/api';

type GenerateReportPageProps = {
  canSwitchCompany: boolean;
  canManageSpirisConnections?: boolean;
  onSwitchCompany: () => void;
  onManageSpirisConnections?: () => void;
  currentProfileKey: string;
  currentTarget: ReportTarget | null;
  makrillAnimationEnabled?: boolean;
};

const areReportTargetsEqual = (left: ReportTarget | null, right: ReportTarget | null) => {
  if (left === right) {
    return true;
  }

  if (!left || !right) {
    return false;
  }

  return left.id === right.id
    && left.type === right.type
    && left.label === right.label
    && left.description === right.description
    && left.memberCount === right.memberCount;
};

const areGenerateReportPagePropsEqual = (prev: GenerateReportPageProps, next: GenerateReportPageProps) => {
  return prev.canSwitchCompany === next.canSwitchCompany
    && prev.canManageSpirisConnections === next.canManageSpirisConnections
    && prev.onSwitchCompany === next.onSwitchCompany
    && prev.onManageSpirisConnections === next.onManageSpirisConnections
    && prev.currentProfileKey === next.currentProfileKey
    && prev.makrillAnimationEnabled === next.makrillAnimationEnabled
    && areReportTargetsEqual(prev.currentTarget, next.currentTarget);
};

const GenerateReportPage: React.FC<GenerateReportPageProps> = ({
  canSwitchCompany,
  canManageSpirisConnections = false,
  onSwitchCompany,
  onManageSpirisConnections,
  currentProfileKey,
  currentTarget,
  makrillAnimationEnabled = true,
}) => {
  // Data loading from context
  const {
    costCenters,
    costCentersError,
    companySettings,
    companySettingsError,
    latestClosedPeriod,
    latestClosedPeriodError,
    isLoading: isLoadingScopeData,
    isRedirecting,
    error: scopeDataError,
  } = useReportScopeData();

  const isGroupTarget = currentTarget?.type === 'group';

  const resolvedCompanyName = isGroupTarget
    ? (currentTarget?.label || 'Koncern')
    : (companySettings?.Name || currentProfileKey || 'Bolag');
  const shouldShowCompanyNameImmediately = isGroupTarget && Boolean(resolvedCompanyName);

  const {
    reportScopeKey,
    currentMonthStartLocal,
    previousMonthEndLocal,
    previousMonthStartLocal,
    todayLocal,
    yearToDateStartLocal,
    fiscalYearId,
    startDate,
    endDate,
    reportMode,
    reportLayout,
    selectedUnits,
    dateError,
    selectableUnits,
    isAllSelected,
    handleStartDateChange,
    handleEndDateChange,
    handleFiscalYearChange,
    applyDateRange,
    isPresetActive,
    handleReportModeChange,
    handleReportLayoutChange,
    handleToggleAllUnits,
    handleUnitToggle,
  } = useConfigureReportRequest({
    costCenters,
    currentProfileKey,
    currentTarget,
  });

  useEffect(() => {
    return () => {
      // Cleanup explosion interval
      if (explosionIntervalRef.current) {
        clearInterval(explosionIntervalRef.current);
      }
    };
  }, []);

  // Makrill explosion animation state
  const [explosiveMakrils, setExplosiveMakrils] = useState<Array<{
    id: string;
    x: number;
    y: number;
    index: number;
    sizeMultiplier: number;
    delay: number;
    fadOutStart: number; // percentage
    opacity: number;
    startX: number;
    startY: number;
    animationDuration: number; // milliseconds
  }>>([]);
  const buttonRefForExplosion = useRef<HTMLButtonElement>(null);
  const explosionIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const {
    loading,
    error,
    preflightIssues,
    latestCompletedReport,
    primaryButtonIsDownloadMode,
    generateReport,
    downloadReport,
  } = useRunReportJob({
    currentProfileKey,
    reportScopeKey,
  });

  const isGenerateDisabled = loading
    || isLoadingScopeData
    || !!dateError
    || !!costCentersError
    || (selectableUnits.length > 0 && selectedUnits.size === 0);

  const triggerMakrillExplosion = useCallback(() => {
    if (!makrillAnimationEnabled || !buttonRefForExplosion.current) return;

    const rect = buttonRefForExplosion.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const newMakrils: typeof explosiveMakrils = [];

    // Random number of fish between 35-50 (reduced ~25-30% from 50-70)
    const totalFish = 35 + Math.floor(Math.random() * 16);

    // Create wave pattern: few, many, few (proportional to total)
    // Each wave has its own animation duration for rolling wave effect
    const waves = [
      { ratio: 0.125, delayStart: 0, delayEnd: 50, animationDuration: 600 }, // ~10 fish: quick movement
      { ratio: 0.625, delayStart: 50, delayEnd: 900, animationDuration: 1000 }, // ~50 fish: medium movement
      { ratio: 0.25, delayStart: 900, delayEnd: 1400, animationDuration: 1400 }, // ~20 fish: slow movement
    ];

    const waveCounts = waves.map(w => Math.round(w.ratio * totalFish));

    let fishIndex = 0;
    for (let waveIdx = 0; waveIdx < waves.length; waveIdx++) {
      const wave = waves[waveIdx];
      const count = waveCounts[waveIdx];
      
      for (let i = 0; i < count; i++) {
        const delay = wave.delayStart + Math.random() * (wave.delayEnd - wave.delayStart);
        
        // Start from center area of button, within middle 40% of button width
        // Offset: ±20% of button width (middle 40%)
        const widthOffset = (Math.random() - 0.5) * (rect.width * 0.4);
        const heightOffset = (Math.random() - 0.5) * (rect.height * 0.3); // Slight height variation too
        const startX = centerX + widthOffset;
        const startY = centerY + heightOffset;
        
        // Better distribution: favor West (180°) and East (0°) over North (270°)
        // 20% North, 40% West, 40% East
        const rand = Math.random() * 100;
        let direction;
        if (rand < 20) {
          direction = { baseDegree: 270, label: 'north' };   // Up: 20%
        } else if (rand < 60) {
          direction = { baseDegree: 180, label: 'west' };    // Left: 40%
        } else {
          direction = { baseDegree: 0, label: 'east' };      // Right: 40%
        }
        
        // Add variation around the main direction (±30 degrees)
        const angleVariation = (Math.random() - 0.5) * 60;
        const angle = direction.baseDegree + angleVariation;

        const radians = (angle * Math.PI) / 180;
        // Random distance 260-470px (30% lower than before)
        const distance = 260 + Math.random() * 210;
        const endX = startX + Math.cos(radians) * distance;
        const endY = startY + Math.sin(radians) * distance;

        // Fade out start: 30-60% of travel
        const fadOutStart = 30 + Math.random() * 30;

        newMakrils.push({
          id: `${Date.now()}-${fishIndex}`,
          x: endX,
          y: endY,
          index: fishIndex % 25,
          sizeMultiplier: 0.72 + Math.random() * 1.728,
          delay,
          fadOutStart,
          opacity: 1,
          startX,
          startY,
          animationDuration: wave.animationDuration,
        });
        fishIndex++;
      }
    }

    setExplosiveMakrils(newMakrils);

    // Clear old interval if exists
    if (explosionIntervalRef.current) {
      clearInterval(explosionIntervalRef.current);
    }

    // Calculate max total duration across all waves to know when to cleanup
    const maxTotalDuration = Math.max(...newMakrils.map(m => m.delay + m.animationDuration));
    
    const startTime = Date.now();
    explosionIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;

      if (elapsed > maxTotalDuration + 500) {
        setExplosiveMakrils([]);
        if (explosionIntervalRef.current) {
          clearInterval(explosionIntervalRef.current);
        }
        return;
      }

      setExplosiveMakrils((prev) =>
        prev
          .map((m) => {
            const elapsedSinceStart = elapsed - m.delay;
            if (elapsedSinceStart < 0) return m; // Not started yet

            // Progress through animation (0 to 1) using its specific duration
            const progress = Math.min(elapsedSinceStart / m.animationDuration, 1);

            // Start fading out at fadeOutStart percentage
            let opacity = 1;
            if (progress > m.fadOutStart / 100) {
              // Fade starts at fadOutStart and completes at fadOutStart + 20
              const fadeProgress = (progress - m.fadOutStart / 100) / 0.2;
              opacity = Math.max(0, 1 - fadeProgress);
            }

            return { ...m, opacity };
          })
          .filter((m) => m.opacity > 0.01)
      );
    }, 30);
  }, [makrillAnimationEnabled]);

  const handleGenerateReport = useCallback(async () => {
    const request: ReportRequest = {
      startDate,
      endDate,
      mode: reportMode,
      layout: reportLayout,
      resultUnits: Array.from(selectedUnits),
      fiscalYearId,
    };

    await generateReport({
      request,
      requiresPreflight: isGroupTarget,
      validationError: dateError ? 'Rätta datum.' : null,
    });
  }, [dateError, endDate, fiscalYearId, generateReport, isGroupTarget, reportLayout, reportMode, selectedUnits, startDate]);

  const handleDownloadReport = useCallback(async (report: ReportStatus) => {
    await downloadReport(report, { onBeforeDownload: triggerMakrillExplosion });
  }, [downloadReport, triggerMakrillExplosion]);

  return (
    <Container
      maxWidth="lg"
      sx={{
        py: 4,
        pb: 8,
        backgroundColor: 'background.default',
        minHeight: '100%',
      }}
    >
      <Box>
        {/* Error Alert */}
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              py: 1.5,
            }}
            icon={<ErrorIcon />}
          >
            {error}
          </Alert>
        )}

        {/* Makrill explosion animation */}
        {explosiveMakrils.map((makrill) => {
          const size = 30 * makrill.sizeMultiplier;
          const tx = makrill.x - makrill.startX;
          const ty = makrill.y - makrill.startY;

          return (
            <Box
              key={makrill.id}
              className="makrill-explode"
              data-index={makrill.index % 25}
              sx={{
                '--tx': `${tx}px`,
                '--ty': `${ty}px`,
                width: size,
                height: size,
                left: makrill.startX - size / 2,
                top: makrill.startY - size / 2,
                opacity: makrill.opacity,
                animation: `makrillExplode${makrill.index % 25} ${makrill.animationDuration / 1000}s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards`,
                animationDelay: `${makrill.delay}ms`,
              } as any}
            >
              <Box
                component="img"
                src="/gais-makrill.png"
                alt=""
                aria-hidden="true"
                sx={{ width: size, height: size }}
              />
            </Box>
          );
        })}

        {/* Redirecting to Spiris */}
        {isRedirecting ? (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              background: (theme) => theme.palette.mode === 'dark'
                ? 'rgba(30, 41, 59, 0.6)'
                : 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              animation: 'fadeInUp 0.6s ease-out',
              boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 1px 3px rgba(0, 0, 0, 0.12)' : '0 1px 2px rgba(0, 0, 0, 0.05)',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
              <CircularProgress />
              <Typography variant="body1" color="textSecondary">
                Omdirigerar till Spiris...
              </Typography>
            </Box>
          </Paper>
        ) : (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              background: (theme) => theme.palette.mode === 'dark'
                ? 'rgba(30, 41, 59, 0.6)'
                : 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              animation: 'fadeInUp 0.6s ease-out',
              boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 1px 3px rgba(0, 0, 0, 0.12)' : '0 1px 2px rgba(0, 0, 0, 0.05)',
            }}
          >
            <ReportHeaderSection
              isLoadingData={isLoadingScopeData}
              shouldShowCompanyNameImmediately={shouldShowCompanyNameImmediately}
              resolvedCompanyName={resolvedCompanyName}
              canSwitchCompany={canSwitchCompany}
              canManageSpirisConnections={canManageSpirisConnections}
              onSwitchCompany={onSwitchCompany}
              onManageSpirisConnections={onManageSpirisConnections}
            />

            {/* Show loading state for data */}
            {scopeDataError ? (
              <Alert severity="error" sx={{ mb: 4 }}>
                {scopeDataError}
              </Alert>
            ) : (
              <>
                {companySettingsError && !isGroupTarget && (
                  <Alert severity="warning" sx={{ mb: 4 }}>
                    {companySettingsError}
                  </Alert>
                )}
                <Stack spacing={4}>
                  <ReportSettingsSection
                    reportMode={reportMode}
                    onReportModeChange={handleReportModeChange}
                    reportLayout={reportLayout}
                    onReportLayoutChange={handleReportLayoutChange}
                    fiscalYearId={fiscalYearId}
                    onFiscalYearChange={handleFiscalYearChange}
                    previousMonthStartLocal={previousMonthStartLocal}
                    previousMonthEndLocal={previousMonthEndLocal}
                    currentMonthStartLocal={currentMonthStartLocal}
                    todayLocal={todayLocal}
                    yearToDateStartLocal={yearToDateStartLocal}
                    latestClosedPeriod={latestClosedPeriod}
                    latestClosedPeriodError={latestClosedPeriodError}
                    isGroupTarget={isGroupTarget}
                    isPresetActive={isPresetActive}
                    applyDateRange={applyDateRange}
                    dateError={dateError}
                    startDate={startDate}
                    endDate={endDate}
                    onStartDateChange={handleStartDateChange}
                    onEndDateChange={handleEndDateChange}
                  />
                  <ResultUnitsSection
                    isLoadingData={isLoadingScopeData}
                    loadError={costCentersError}
                    selectableUnits={selectableUnits}
                    selectedUnits={selectedUnits}
                    isAllSelected={isAllSelected}
                    onToggleAllUnits={handleToggleAllUnits}
                    onUnitToggle={handleUnitToggle}
                  />
                  <ReportActionsSection
                    preflightIssues={preflightIssues}
                    primaryButtonIsDownloadMode={primaryButtonIsDownloadMode}
                    latestCompletedReport={latestCompletedReport}
                    onDownloadReport={handleDownloadReport}
                    onGenerateReport={handleGenerateReport}
                    loading={loading}
                    isGenerateDisabled={isGenerateDisabled}
                    selectedUnitCount={selectedUnits.size}
                    buttonRefForExplosion={buttonRefForExplosion as React.RefObject<HTMLButtonElement>}
                  />
                </Stack>
              </>
            )}
          </Paper>
        )}
      </Box>
    </Container>
  );
};

export default React.memo(GenerateReportPage, areGenerateReportPagePropsEqual);